import { useEffect, useMemo } from 'react';
import type { ElementNodeDto } from '@xshop/shared';
import { boolProp, numberProp, textProp } from '../render/elementProps';
import { useFormOrIsland, type FieldRule, type FieldShape, type FieldValue, type FormAnswers } from './formScope';

// Names a stable DOM id for one part of one field, so a label and the control it names find each other.
const partId = (nodeId: string, part: string): string => `xs-${nodeId}-${part}`;

/**
 * Joining one field element to the form around it.
 *
 * Every control in this folder asks for the same five things — the rules its merchant set, the answer
 * held so far, the message to show under it, the call that writes a new answer, and the id a label and
 * an error point at. They are resolved once here so no control invents its own name for a field, its
 * own id for a label to reach, or its own idea of what `required` means.
 */

/** What one drawn control needs: its rule, its answer, its message, and where that message is drawn. */
export type BoundField = {
  rule: FieldRule;
  value: FieldValue;
  error: string;
  onChange: (value: FieldValue) => void;
  /** Where the message is drawn, so a control can point a screen reader at it while it is showing. */
  errorId: string;
  form: FormAnswers;
};

/** What a field element is beyond its node: the shape its answers take, and what it holds before any. */
export type FieldBinding = { shape: FieldShape; empty: FieldValue };

// Reads a bound the merchant set, answering null when they set none so nothing nobody asked for is checked.
// Zero is "no bound", not a bound of nothing: the inspector's number control has to land on some value,
// and "at most 0 characters" would refuse every answer a shopper could possibly type.
function optionalCount(props: Readonly<Record<string, unknown>>, key: string): number | null {
  const raw = props[key];
  if (raw === undefined || raw === null || raw === '') return null;
  const value = numberProp(props, key, Number.NaN);
  return Number.isFinite(value) && value > 0 ? Math.trunc(value) : null;
}

// Names one field: the merchant's own word for it, or the node's id so two unnamed fields never collide.
export const fieldName = (node: ElementNodeDto): string => textProp(node.props, 'name', node.id);

// Builds the rules one field registers, out of that field's own settings and nothing else.
export function fieldRuleOf(node: ElementNodeDto, shape: FieldShape): FieldRule {
  const name = fieldName(node);
  return {
    name,
    label: textProp(node.props, 'label', ''),
    placeholder: textProp(node.props, 'placeholder', ''),
    domId: partId(node.id, 'field'),
    required: boolProp(node.props, 'required', false),
    minLength: optionalCount(node.props, 'minLength'),
    maxLength: optionalCount(node.props, 'maxLength'),
    shape
  };
}

// Holds one field's rules steady between renders, so registering them is not an endless round trip.
function useFormRule(node: ElementNodeDto, shape: FieldShape): FieldRule {
  const built = fieldRuleOf(node, shape);
  const flat = [built.name, built.label, built.placeholder, built.domId, built.required, built.minLength, built.maxLength, built.shape].join(' ');
  // The key is every field of the rule spelled flat, which is what makes rebuilding it each render harmless.
  return useMemo(() => built, [flat]);
}

// Binds one field node to the form around it, registering its rules for as long as it is drawn.
export function useFormField(node: ElementNodeDto, binding: FieldBinding): BoundField {
  const form = useFormOrIsland();
  const rule = useFormRule(node, binding.shape);
  const { register } = form;
  useEffect(() => register(rule), [register, rule]);
  const held = form.values[rule.name];
  return {
    rule,
    value: held === undefined ? binding.empty : held,
    error: form.errors[rule.name] ?? '',
    onChange: (value: FieldValue) => form.setValue(rule.name, value),
    errorId: partId(node.id, 'error'),
    form
  };
}
